import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Container, Table, Form, InputGroup, Button, Row, Col, Pagination, Badge, Modal, Spinner, Alert } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import apiClient from '../../../../api/apiClient';
import distributorService from '../../../../services/DistributorService';
import { showToast } from '../../../../utils/toast';
import { beatsCache } from '../../../../utils/beatsCache';

const DistributorFOS = () => {
  const [fosList, setFosList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedFOS, setSelectedFOS] = useState(null);
  const [beats, setBeats] = useState([]);
  const [beatsLoading, setBeatsLoading] = useState(false);
  const beatsLoadedRef = useRef(false);

  // Helper function to format login time to 12-hour format
  const formatLoginTime = (loginDisplay) => {
    if (!loginDisplay || loginDisplay === 'Never') return 'Never';
    
    // Check if it's a date-time string
    if (loginDisplay.includes(' ')) {
      const [datePart, timePart] = loginDisplay.split(' ');
      if (timePart) {
        const date = new Date(`${datePart}T${timePart}`);
        if (!isNaN(date)) {
          return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
        }
      }
    }
    
    return loginDisplay;
  };
  
  const [formData, setFormData] = useState({
    mobile: '',
    password: '',
    customer_id: '',
    name: '',
    email: '',
    status: 'active',
    beat_ids: []
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [validated, setValidated] = useState(false);

  // Fetch FOS users from API
  const fetchFOSUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: rowsPerPage
      };
      
      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.status = statusFilter;
      
      const response = await apiClient.get('/api/distributor/fos/', { params });
      
      if (response.data) {
        setFosList(response.data.results || []);
        setTotalCount(response.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching FOS users:', error);
      showToast.error('Failed to fetch FOS users');
    } finally {
      setLoading(false);
    }
  }, [currentPage, rowsPerPage, searchQuery, statusFilter]);

  // Fetch Beats from API using cache
  const fetchBeats = useCallback(async () => {
    if (beatsLoadedRef.current) {
      return; // Skip if already loaded locally
    }
    
    setBeatsLoading(true);
    try {
      const response = await beatsCache.getBeats(async () => {
        return await distributorService.getBeats({ page_size: 100 });
      });
      
      if (response.success) {
        setBeats(response.data.results || []);
        beatsLoadedRef.current = true; // Mark as loaded locally
      } else {
        showToast.error(response.error || 'Failed to fetch beats');
      }
    } catch (error) {
      console.error('Error fetching beats:', error);
      showToast.error('Failed to fetch beats');
    } finally {
      setBeatsLoading(false);
    }
  }, []);

  // Debounced effect for FOS users
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchFOSUsers();
    }, searchQuery ? 300 : 0);

    return () => clearTimeout(timer);
  }, [fetchFOSUsers]);

  // Effect for beats (only on mount)
  useEffect(() => {
    fetchBeats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };
  
  const handleFilterChange = (filterType, value) => {
    if (filterType === 'status') setStatusFilter(value);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };


  const exportToExcel = () => {
    const exportData = fosList.map(fos => ({
      'FOS ID': fos.customer_id || '',
      'Name': fos.name || '',
      'Email': fos.email || '',
      'Contact': fos.mobile || '',
      'Assigned Beats': fos.assigned_beats && fos.assigned_beats.length > 0 
        ? fos.assigned_beats.map(beat => beat.beat_name).join(', ') 
        : 'No beats assigned',
      'Status': fos.status || '',
      'Last Login': formatLoginTime(fos.last_login_display)
    }));
    
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'FOS');
    XLSX.writeFile(workbook, `fos_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleAdd = () => {
    setSelectedFOS(null);
    setModalMode('add');
    setFormData({
      mobile: '',
      password: '',
      customer_id: '',
      name: '',
      email: '',
      postal_code: '',
      status: 'active',
      beat_ids: []
    });
    setFormErrors({});
    setValidated(false);
    setShowModal(true);
  };

  const handleEdit = (fos) => {
    setSelectedFOS(fos);
    setModalMode('edit');
    setFormData({
      mobile: fos.mobile,
      password: '', // Don't populate password for edit
      customer_id: fos.customer_id || '',
      name: fos.name,
      email: fos.email || '',
      status: fos.status,
      beat_ids: fos.assigned_beats ? fos.assigned_beats.map(beat => beat.beat_id) : []
    });
    setFormErrors({});
    setValidated(false);
    setShowModal(true);
  };

  const handleDelete = async (fos) => {
    if (window.confirm(`Are you sure you want to deactivate ${fos.name}?`)) {
      try {
        await apiClient.delete(`/api/distributor/users/${fos.id}/`);
        showToast.success(`${fos.name} deactivated successfully`);
        fetchFOSUsers();
      } catch (error) {
        console.error('Error deactivating FOS:', error);
        showToast.error('Failed to deactivate FOS');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormErrors({});
    setValidated(true);
    
    try {
      const payload = {
        ...formData,
        role: 'fos'
      };
      
      // Remove empty password for edit mode
      if (modalMode === 'edit' && !payload.password) {
        delete payload.password;
      }
      
      // Ensure beat_ids is an array of integers
      if (payload.beat_ids) {
        payload.beat_ids = payload.beat_ids.map(id => parseInt(id));
      } else {
        payload.beat_ids = [];
      }
      
      let response;
      if (modalMode === 'add') {
        response = await apiClient.post('/api/distributor/fos/', payload);
        showToast.success('FOS created successfully');
      } else {
        response = await apiClient.put(`/api/distributor/fos/${selectedFOS.id}/`, payload);
        showToast.success('FOS updated successfully');
      }
      
      setShowModal(false);
      fetchFOSUsers();
    } catch (error) {
      console.error('Error saving FOS:', error);
      console.log('Error response data:', error.response?.data);
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle the API error structure: { success: false, message: "Invalid data", errors: { mobile: [...] } }
        if (errorData.errors && typeof errorData.errors === 'object') {
          // Handle nested errors structure like { mobile: ["user with this mobile already exists."] }
          setFormErrors(errorData.errors);
          
          // Also show the main message if available
          if (errorData.message) {
            showToast.error(errorData.message);
          }
        } else {
          // Handle direct field errors in response data (fallback)
          const fieldErrors = {};
          let hasFieldErrors = false;
          
          Object.keys(errorData).forEach(key => {
            if (key !== 'success' && key !== 'message' && (Array.isArray(errorData[key]) || typeof errorData[key] === 'string')) {
              fieldErrors[key] = errorData[key];
              hasFieldErrors = true;
            }
          });
          
          if (hasFieldErrors) {
            setFormErrors(fieldErrors);
          }
          
          // Show the main error message
          if (errorData.message) {
            showToast.error(errorData.message);
          } else {
            showToast.error(`Failed to ${modalMode === 'add' ? 'create' : 'update'} FOS`);
          }
        }
      } else {
        showToast.error(`Failed to ${modalMode === 'add' ? 'create' : 'update'} FOS`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle multi-select beat changes
  const handleBeatChange = (beatId, isChecked) => {
    setFormData(prev => {
      const currentBeatIds = prev.beat_ids || [];
      if (isChecked) {
        // Add beat if not already present
        return {
          ...prev,
          beat_ids: currentBeatIds.includes(beatId) ? currentBeatIds : [...currentBeatIds, beatId]
        };
      } else {
        // Remove beat
        return {
          ...prev,
          beat_ids: currentBeatIds.filter(id => id !== beatId)
        };
      }
    });
  };


  const getStatusBadge = (status) => {
    return status === 'active' ? 
      <Badge bg="success">Active</Badge> : 
      <Badge bg="danger">Inactive</Badge>;
  };

  return (
    <Container fluid className="py-1">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ color: '#031a6f', fontWeight: '600' }}>
          <i className="fas fa-users me-2"></i>
          Field Officers (FOS)
        </h5>
        <div className="d-flex gap-2">
          <Button variant="success" onClick={exportToExcel}>
            <i className="fas fa-file-excel me-2"></i>
            Export Excel
          </Button>
          <Button variant="primary" onClick={handleAdd}>
            <i className="fas fa-plus me-2"></i>
            Add FOS
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Row className="mb-4">
        <Col md={3}>
          <InputGroup>
            <InputGroup.Text>
              <i className="fas fa-search"></i>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search FOS..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={2}>
          <Form.Select
            value={statusFilter}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Form.Select>
        </Col>
        <Col md={7}>
          {/* Space for future filters */}
        </Col>
      </Row>

      {/* Table */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover className="modern-table">
            <thead>
              <tr>
                <th>Actions</th>
                <th>FOS ID</th>
                <th>Name</th>
                <th>Contact</th>
                <th>Assigned Beats</th>
                <th>Status</th>
                <th>Last Login</th>
              </tr>
            </thead>
            <tbody>
              {fosList.map((fos) => (
                <tr key={fos.id}>
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEdit(fos)}
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(fos)}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </div>
                  </td>
                  <td><strong>{fos.customer_id}</strong></td>
                  <td>
                    <div>
                      <div>{fos.name}</div>
                      <small className="text-muted">{fos.email}</small>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div>{fos.mobile}</div>
                      <small className="text-muted">ID: {fos.id}</small>
                    </div>
                  </td>
                  <td>
                    <div>
                      {fos.assigned_beats && fos.assigned_beats.length > 0 ? (
                        fos.assigned_beats.map((beat) => (
                          <Badge key={beat.beat_id} bg="info" className="me-1 mb-1">
                            {beat.beat_name}
                          </Badge>
                        ))
                      ) : (
                        <small className="text-muted">No beats assigned</small>
                      )}
                    </div>
                  </td>
                  <td>{getStatusBadge(fos.status)}</td>
                  <td>
                    <small className="text-muted">
                      {formatLoginTime(fos.last_login_display)}
                    </small>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          {fosList.length === 0 && (
            <div className="text-center py-4">
              <p className="text-muted">No FOS users found</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div>
          <Form.Select
            value={rowsPerPage}
            onChange={(e) => {
              const newRowsPerPage = Number(e.target.value);
              setRowsPerPage(newRowsPerPage);
              setCurrentPage(1);
            }}
            style={{ width: 'auto' }}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </Form.Select>
        </div>
        <div>
          Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount} entries
        </div>
        <Pagination>
          <Pagination.First 
            onClick={() => setCurrentPage(1)} 
            disabled={currentPage === 1} 
          />
          <Pagination.Prev 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
            disabled={currentPage === 1} 
          />
          {[...Array(Math.min(5, totalPages))].map((_, index) => {
            const pageNum = Math.max(1, Math.min(currentPage - 2 + index, totalPages - 4 + index));
            return (
              <Pagination.Item
                key={pageNum}
                active={pageNum === currentPage}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum}
              </Pagination.Item>
            );
          })}
          <Pagination.Next 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
            disabled={currentPage === totalPages} 
          />
          <Pagination.Last 
            onClick={() => setCurrentPage(totalPages)} 
            disabled={currentPage === totalPages} 
          />
        </Pagination>
      </div>

      {/* FOS Form Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'add' ? 'Add New' : 'Edit'} Field Officer</Modal.Title>
        </Modal.Header>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Modal.Body>
            
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Mobile Number *</Form.Label>
                  <Form.Control
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => handleInputChange('mobile', e.target.value)}
                    placeholder="92XXXXXXXX"
                    maxLength={10}
                    // disabled={modalMode === 'edit'}
                    isInvalid={!!formErrors.mobile}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.mobile && (Array.isArray(formErrors.mobile) ? formErrors.mobile.join(', ') : formErrors.mobile)}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              
              {modalMode === 'add' && (
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Password *</Form.Label>
                    <Form.Control
                      type="password"
                      autoComplete="off"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Enter password"
                      isInvalid={!!formErrors.password}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {formErrors.password && (Array.isArray(formErrors.password) ? formErrors.password.join(', ') : formErrors.password)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              )}
              
              {modalMode === 'edit' && (
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>New Password (optional)</Form.Label>
                    <Form.Control
                      type="password"
                      autoComplete="off"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Enter new password to change"
                      isInvalid={!!formErrors.password}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formErrors.password && (Array.isArray(formErrors.password) ? formErrors.password.join(', ') : formErrors.password)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              )}
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>FOS ID</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.customer_id}
                    onChange={(e) => handleInputChange('customer_id', e.target.value)}
                    placeholder="FOS PRM ID"
                    autoComplete="off"
                    // disabled={modalMode === 'edit'}
                    isInvalid={!!formErrors.customer_id}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.customer_id && (Array.isArray(formErrors.customer_id) ? formErrors.customer_id.join(', ') : formErrors.customer_id)}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Full Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter full name"
                    isInvalid={!!formErrors.name}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.name && (Array.isArray(formErrors.name) ? formErrors.name.join(', ') : formErrors.name)}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter email address"
                    isInvalid={!!formErrors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.email && (Array.isArray(formErrors.email) ? formErrors.email.join(', ') : formErrors.email)}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Assigned Beats</Form.Label>
                  {beatsLoading ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading beats...
                    </div>
                  ) : (
                    <div className="border rounded p-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {beats.length === 0 ? (
                        <small className="text-muted">No beats available</small>
                      ) : (
                        <Row>
                          {beats.map((beat) => (
                            <Col md={6} key={beat.id} className="mb-2">
                              <Form.Check
                                type="checkbox"
                                id={`beat-${beat.id}`}
                                label={beat.beat_name}
                                checked={formData.beat_ids.includes(beat.id)}
                                onChange={(e) => handleBeatChange(beat.id, e.target.checked)}
                              />
                            </Col>
                          ))}
                        </Row>
                      )}
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    Select one or more beats for this FOS to manage
                  </Form.Text>
                </Form.Group>
              </Col>

            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {modalMode === 'add' ? 'Creating...' : 'Updating...'}
                </>
              ) : (
                <>
                  {modalMode === 'add' ? 'Create' : 'Update'} FOS
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

    </Container>
  );
};

export default DistributorFOS;